import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Components
import Header from "./components/Header";
import Hero from "./components/Hero";
import HowItWorks from "./components/HowItWorks";
import Benefits from "./components/Benefits";
import Calculator from "./components/Calculator";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import ContactForm from "./components/ContactForm";
import LegalSecurity from "./components/LegalSecurity";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieBanner from "./components/CookieBanner";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";

// Legal pages
import { AvisoLegal, PoliticaPrivacidad, TerminosCondiciones, PoliticaCookies } from "./components/LegalPages";

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <div id="hero">
          <Hero />
        </div>
        <div id="how-it-works">
          <HowItWorks />
        </div>
        <div id="benefits">
          <Benefits />
        </div>
        <div id="calculator">
          <Calculator />
        </div>
        <div id="testimonials">
          <Testimonials />
        </div>
        <div id="faq">
          <FAQ />
        </div>
        <LegalSecurity />
        <ContactForm />
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTop />
      <CookieBanner />
      <Toaster />
    </div>
  );
};

// SEO Component
const SEOHead = ({ title, description, image, url }) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      let meta = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', 'alquiler adelantado, alquiler prepago, ahorro alquiler, propietarios, inquilinos, madrid, barcelona');
    updateMetaTag('author', 'URBAN RUES S.L');
    updateMetaTag('robots', 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'Alquiler Adelantado', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Additional SEO tags
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('theme-color', '#16a34a');

    // Structured data for local business
    let structuredData = document.querySelector('#structured-data');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    
    structuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Alquiler Adelantado - URBAN RUES S.L",
      "image": image,
      "description": description,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Calle Ávila Nº6 1D",
        "addressLocality": "Móstoles",
        "addressRegion": "Madrid",
        "postalCode": "28937",
        "addressCountry": "ES"
      },
      "telephone": "+34 684 059 141",
      "email": "info@contactoadelantado.com",
      "url": url,
      "openingHours": "Mo-Fr 09:00-18:00",
      "priceRange": "€€",
      "paymentAccepted": "Cash, Credit Card, Bank Transfer"
    });

  }, [title, description, image, url]);

  return null;
};

function App() {
  const seoData = {
    title: "Alquiler Adelantado - Ahorra 30% pagando 2 años por adelantado | URBAN RUES",
    description: "Conectamos propietarios e inquilinos para alquileres con 30% de descuento. Pago anticipado por 24 meses. Proceso seguro y transparente. ¡Solicita información!",
    image: "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnR8ZW58MHx8fHwxNzU0NTg0NDY2fDA&ixlib=rb-4.1.0&q=85",
    url: window.location.href
  };

  return (
    <div className="App">
      <BrowserRouter>
        <SEOHead {...seoData} />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;